import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Grid, Paper, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import { showErrorToast } from "../../../components/common/ShowToast";

interface Booking {
  id: number;
  title: string;
}

interface Venue {
  id: number;
  name: string;
}

interface CrewMember {
  id: number;
  user: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface Role {
  id: number;
  name: string;
}

interface Props {
  formData: {
    stream_id?: number | null;
    booking: number | null;
    title?: string;
    serviceType: string;
    startTime: string;
    endTime: string;
    date: string;
    venue: number | null;
    specialNotes: string;
    travelDuration: number | null;
    callTime: string;
    travellingFrom: string;
    travellingTo: string;
    setupFrom: string;
    setupTo: string;
    estimatedWrap: string;
    priest_name: string;
    mc_name: string;
    eulogist_name: string;
    other: string;
    crew: {
      members: Array<{
        crew_member: number;
        role: number;
        tools: number[];
        skills: number[];
      }>;
    };
    call_sheets: Array<{
      crew_member_id: number;
      assignment: { details?: string; type?: string };
    }>;
    thumbnails: File[];
    slideshows: File[];
    programs: File[];
  };
}

const InfoGrid = ({
  data,
  columns = 3,
}: {
  data: Record<string, string | number | null | undefined>;
  columns?: number;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Grid container spacing={2}>
      {Object.entries(data).map(([key, value]) => (
        <Grid size={{ xs: 12, md: 12 / columns, sm: 6 }} key={key}>
          <Typography variant="body2" sx={{ color: colors.primary[300] }}>
            {key}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {value || "N/A"}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};

const AddStreamConfirmation: React.FC<Props> = ({ formData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [bookingName, setBookingName] = useState<string>("N/A");
  const [venueName, setVenueName] = useState<string>("N/A");
  const [crewMembers, setCrewMembers] = useState<{ [key: number]: string }>({});
  const [roles, setRoles] = useState<{ [key: number]: string }>({});
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch display names for booking, venue, crew members, roles, tools, and skills
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch booking name
        if (formData.booking) {
          const bookingRes = await apiService.get<Booking>(
            `/api/bookings/booking/${formData.booking}/`
          );
          setBookingName(bookingRes.data.title || "N/A");
        } else {
          setBookingName("N/A");
        }

        // Fetch venue name
        if (formData.venue) {
          const venueRes = await apiService.get<Venue>(
            `/api/venues/venue/${formData.venue}/`
          );
          setVenueName(venueRes.data.name || "N/A");
        } else {
          setVenueName("N/A");
        }

        // Fetch crew members
        const crewIds = formData.crew.members
          .map((m) => m.crew_member)
          .filter((id) => id > 0);
        if (crewIds.length > 0) {
          const crewRes = await Promise.all(
            crewIds.map((id) =>
              apiService.get<CrewMember>(`/api/crews/crew-members/${id}/`)
            )
          );
          const crewMap = crewRes.reduce((acc, res) => {
            const member = res.data;
            acc[member.id] =
              `${member.user.first_name} ${member.user.last_name}`.trim() ||
              member.user.email;
            return acc;
          }, {} as { [key: number]: string });
          setCrewMembers(crewMap);
        }

        // Fetch roles
        const roleIds = formData.crew.members
          .map((m) => m.role)
          .filter((id) => id > 0);
        if (roleIds.length > 0) {
          const rolesRes = await apiService.get<{ results: Role[] }>(
            "/api/crews/roles/"
          );
          const roleMap = rolesRes.data.results.reduce((acc, role) => {
            acc[role.id] = role.name;
            return acc;
          }, {} as { [key: number]: string });
          setRoles(roleMap);
        }

      } catch (err) {
        console.error("Failed to fetch confirmation data:", err);
        showErrorToast(
          "Failed to load confirmation details. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    formData.booking,
    formData.venue,
    formData.crew.members,
    formData.call_sheets,
  ]);

  // Memoize the info objects to prevent unnecessary re-renders
  const streamDetailsInfo = useMemo(
    () => ({
      "Booking For": bookingName,
      Title: formData.title || "N/A",
      "Service Type": formData.serviceType || "N/A",
      Venue: venueName,
      Date: formData.date || "N/A",
      "Start Time": formData.startTime || "N/A",
      "End Time": formData.endTime || "N/A",
      "Special Notes": formData.specialNotes || "N/A",
      Priest: formData.priest_name || "N/A",
      MC: formData.mc_name || "N/A",
      Eulogist: formData.eulogist_name || "N/A",
      "Other Key Person": formData.other || "N/A",
    }),
    [bookingName, venueName, formData]
  );

  const travelSetupInfo = useMemo(
    () => ({
      "Call Time": formData.callTime || "N/A",
      "Travel Duration": formData.travelDuration
        ? `${formData.travelDuration} minutes`
        : "N/A",
      "Travelling From": formData.travellingFrom || "N/A",
      "Travelling To": formData.travellingTo || "N/A",
      "Setup From": formData.setupFrom || "N/A",
      "Setup To": formData.setupTo || "N/A",
      "Estimated Wrap": formData.estimatedWrap || "N/A",
    }),
    [formData]
  );

  const teamAssignmentInfo = useMemo(
    () =>
      formData.crew.members.reduce((acc, member) => {
        if (member.crew_member && member.role) {
          const roleName = roles[member.role] || "Unknown Role";
          const crewName = crewMembers[member.crew_member] || "Unknown Member";

          acc[`${roleName} - ${crewName}`] = "Assigned";
        }
        return acc;
      }, {} as Record<string, string>),
    [formData.crew.members, roles, crewMembers]
  );

  const mediaInfo = useMemo(
    () => ({
      Thumbnails: formData.thumbnails.map((f) => f.name).join(", ") || "N/A",
      Slideshows: formData.slideshows.map((f) => f.name).join(", ") || "N/A",
      Programs: formData.programs.map((f) => f.name).join(", ") || "N/A",
    }),
    [formData.thumbnails, formData.slideshows, formData.programs]
  );

  const sectionStyle = {
    p: 2,
    border: `1px solid ${colors.primary[800]}`,
    borderRadius: 2,
    mb: 3,
    height: "100%",
    boxShadow: 0,
    backgroundColor: colors.primary[900],
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <Typography>Loading confirmation details...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Stream Confirmation
      </Typography>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {/* Stream Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Stream Details
            </Typography>
            <InfoGrid data={streamDetailsInfo} />
          </Paper>
        </Grid>

        {/* Travel and Setup */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Travel & Setup Information
            </Typography>
            <InfoGrid data={travelSetupInfo} />
          </Paper>
        </Grid>

        {/* Team Assignment */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Team Assignment
            </Typography>
            {Object.keys(teamAssignmentInfo).length > 0 ? (
              Object.entries(teamAssignmentInfo).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {key}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Box>
              ))
            ) : (
              <Typography>No team members assigned.</Typography>
            )}
          </Paper>
        </Grid>

        {/* Graphics & Media */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Graphics & Media
            </Typography>
            <InfoGrid data={mediaInfo} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddStreamConfirmation;
